import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { UploadState } from '@dxp/core/types';

@Component({
    selector: 'telxl-shared-dropzone',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    templateUrl: './dropzone-panel.component.html',
    styleUrl: './dropzone-panel.component.scss',
})
export class TelXLDropzonePanelComponent {
    @ViewChild('fileInput', { static: true, read: ElementRef })
    private fileInputRef!: ElementRef<HTMLInputElement>;

    current = input<File[]>([]);
    status = input.required<UploadState>();
    maxSize = input.required<string>();
    dropped = output<File[]>();

    readonly isDragging = signal<boolean>(false);

    icon = computed<string>(() => {
        switch (this.status()) {
            case 'upload':
                return 'fa-paperclip';
            case 'unsupported':
                return 'fa-circle-exclamation';
            case 'toolarge':
                return 'fa-triangle-exclamation';
            case 'duplicate':
                return 'fa-files';
        }
    });
    heading = computed<string>(() => {
        switch (this.status()) {
            case 'upload':
                return 'Click or drag files to this area to upload';
            case 'unsupported':
                return 'Unsupported file type';
            case 'toolarge':
                return 'File size is too large';
            case 'duplicate':
                return 'Duplicate files uploaded';
        }
    });
    subHeading = computed<string>(() => {
        switch (this.status()) {
            case 'upload':
                return `Support for a single or bulk upload. Maximum file size ${this.maxSize()}MB.`;
            case 'unsupported':
                return "This file isn't supported. Please upload a supported format.";
            case 'toolarge':
                return `The files exceed the ${this.maxSize()}MB size limit and cannot be uploaded. Please choose a smaller file.`;
            case 'duplicate':
                return 'Please remove the duplicate file before attaching.';
        }
    });

    onHostClick() {
        if (this.status() !== 'upload') return;
        this.fileInputRef.nativeElement.click();
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (this.status() !== 'upload') return;

        if (event.dataTransfer) {
            const types = event.dataTransfer.types;

            if (!types || types.includes('Files')) {
                this.isDragging.set(true);
            }
        } else {
            this.isDragging.set(true);
        }
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (this.status() !== 'upload') return;
        this.isDragging.set(false);
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (this.status() !== 'upload') return;

        this.isDragging.set(false);

        const dt = event.dataTransfer;
        if (!dt) return;

        const files = dt.files;
        if (files && files.length > 0) {
            this.#handleFiles(files);
        }
    }

    onInputChange(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files) return;
        this.#handleFiles(input.files);

        input.value = '';
    }

    #handleFiles(filelist: FileList) {
        this.dropped.emit(Array.from(filelist));
    }
}
